import type { RuntimePlatform } from "./runtime-platform";

export type NativeBuildKind = "acceptance" | "release";

export interface NativeBuildMetadata {
  readonly acceptanceSeed: string;
  readonly buildId: string;
  readonly buildKind: NativeBuildKind;
  readonly buildNumber: string;
  readonly sourceRevision: string;
  readonly version: string;
}

export const nativeBuildMetadataNames = {
  acceptanceSeed: "snakely-native-acceptance-seed",
  buildId: "snakely-native-build-id",
  buildKind: "snakely-native-build-kind",
  buildNumber: "snakely-native-build-number",
  sourceRevision: "snakely-native-source-revision",
  version: "snakely-native-version",
} as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const parseNativeBuildMetadata = (
  value: unknown,
): NativeBuildMetadata => {
  if (!isRecord(value)) {
    throw new Error("Native build metadata must be an object");
  }
  const acceptanceSeed = value.acceptanceSeed;
  const buildId = value.buildId;
  const buildKind = value.buildKind;
  const buildNumber = value.buildNumber;
  const sourceRevision = value.sourceRevision;
  const version = value.version;
  if (
    typeof acceptanceSeed !== "string" ||
    typeof buildId !== "string" ||
    !/^[0-9a-f]{64}$/u.test(buildId) ||
    (buildKind !== "acceptance" && buildKind !== "release") ||
    typeof buildNumber !== "string" ||
    !/^[1-9][0-9]*$/u.test(buildNumber) ||
    typeof sourceRevision !== "string" ||
    !/^[0-9a-f]{8}$/u.test(sourceRevision) ||
    typeof version !== "string" ||
    !/^[0-9]+\.[0-9]+(?:\.[0-9]+)?$/u.test(version)
  ) {
    throw new Error("Native build metadata is invalid");
  }
  if (
    (buildKind === "acceptance" && acceptanceSeed.length === 0) ||
    (buildKind === "release" && acceptanceSeed.length !== 0)
  ) {
    throw new Error("Native acceptance seed does not match the build kind");
  }
  return {
    acceptanceSeed,
    buildId,
    buildKind,
    buildNumber,
    sourceRevision,
    version,
  };
};

const readMetadataValue = (owner: Document, name: string): string => {
  const value = owner
    .querySelector(`meta[name="${name}"]`)
    ?.getAttribute("content");
  if (value === null || value === undefined) {
    throw new Error(`Native build metadata is missing ${name}`);
  }
  return value;
};

export const readNativeBuildMetadata = (
  owner: Document,
  platform: RuntimePlatform,
): NativeBuildMetadata | null =>
  platform.isNative
    ? parseNativeBuildMetadata({
        acceptanceSeed: readMetadataValue(
          owner,
          nativeBuildMetadataNames.acceptanceSeed,
        ),
        buildId: readMetadataValue(owner, nativeBuildMetadataNames.buildId),
        buildKind: readMetadataValue(owner, nativeBuildMetadataNames.buildKind),
        buildNumber: readMetadataValue(
          owner,
          nativeBuildMetadataNames.buildNumber,
        ),
        sourceRevision: readMetadataValue(
          owner,
          nativeBuildMetadataNames.sourceRevision,
        ),
        version: readMetadataValue(owner, nativeBuildMetadataNames.version),
      })
    : null;

export const installedVersionLabel = (
  webLabel: string,
  nativeMetadata: NativeBuildMetadata | null,
): string =>
  nativeMetadata === null
    ? webLabel
    : `Version ${nativeMetadata.version} (Build ${nativeMetadata.buildNumber})`;
